/**
 * @module features/client-device-management/pages/ClientDeviceClientsPage
 * @description Entry page for Client Device Management listing ONLY Active-status
 * client organizations with Logo, Client Name (ID underneath), Contact Person name,
 * City & State, and a "Manage" button navigating to `{Org} Device Management`.
 */
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Building2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { PageHeader } from "@/components/layout";
import { SectionErrorBoundary } from "@/components/feedback/SectionErrorBoundary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClientSummary, useClients } from "@/features/clients";
import { AppError } from "@/lib/api-client";
import { ActiveClientsList, ActiveClientsSkeleton } from "../components/ActiveClientsList";

const PAGE_SIZE = 10;

export function ClientDeviceClientsPage() {
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState("");
  // Defer rapid keystrokes so table filtering remains smooth at 60fps on mobile
  const deferredSearch = React.useDeferredValue(searchInput.trim());

  const {
    data: clientsResponse,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useClients({
    page,
    page_size: PAGE_SIZE,
    status: "active",
    search: deferredSearch || undefined,
  });

  // Defensive client-side guard ensuring strictly Active status clients are displayed
  const activeClients = React.useMemo(() => {
    const items = Array.isArray(clientsResponse?.items) ? clientsResponse.items : [];
    return items.filter((client) => client.status === "active");
  }, [clientsResponse?.items]);

  const pagination = clientsResponse?.pagination;
  const hasSearchFilter = Boolean(searchInput.trim());
  const appError = error ? AppError.fromUnknown(error) : null;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const nextValue = e.target.value;
    setSearchInput(nextValue);
    React.startTransition(() => {
      setPage(1);
    });
  };

  const handleClearSearch = (): void => {
    setSearchInput("");
    React.startTransition(() => {
      setPage(1);
    });
  };

  const handleManageClient = React.useCallback(
    (client: ClientSummary): void => {
      navigate(`/s-admin/client-devices/${encodeURIComponent(client.id)}`, {
        state: {
          clientSnapshot: {
            id: client.id,
            org_name: client.org_name,
            logo_url: client.logo_url,
            city: client.city,
            state: client.state,
            contact_person_name: client.contact_person_name,
          },
        },
      });
    },
    [navigate]
  );

  const handleRefresh = (): void => {
    void refetch();
  };

  return (
    <SectionErrorBoundary sectionTitle="Unable to display Client Device Management">
      <div className="space-y-4 sm:space-y-6">
        <PageHeader
          title="Client Device Management"
          description="Select an active client organization to manage its assigned devices, configurations, WiFi profiles, and quotas."
          actions={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
              className="h-10 sm:h-9 touch:min-h-[44px] text-xs gap-1.5"
              aria-label="Refresh active client organizations"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`}
                aria-hidden="true"
              />
              <span>Refresh</span>
            </Button>
          }
        />

        {/* Search Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="relative w-full sm:max-w-md">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            {/* Why text-base sm:text-sm: iOS Safari zooms the viewport if focused input font-size < 16px */}
            <Input
              type="search"
              inputMode="search"
              autoComplete="off"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search active clients by name, contact, or city..."
              aria-label="Search active client organizations"
              className="pl-9 pr-9 h-11 sm:h-10 text-base sm:text-sm rounded-xl bg-card"
            />
            {hasSearchFilter && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClearSearch}
                aria-label="Clear search query"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>

          {!isLoading && !isError && pagination && (
            <p className="text-xs text-muted-foreground tabular-nums">
              Showing{" "}
              <span className="font-medium text-foreground">
                {activeClients.length}
              </span>{" "}
              active {activeClients.length === 1 ? "organization" : "organizations"}
            </p>
          )}
        </div>

        {/* 4-State UI Completeness Contract */}
        {isLoading ? (
          <ActiveClientsSkeleton rows={6} />
        ) : isError ? (
          <div
            role="alert"
            className="rounded-2xl border border-danger/20 bg-danger-soft p-6 sm:p-8 text-center text-danger shadow-card"
          >
            <AlertCircle className="mx-auto h-8 w-8 text-danger" aria-hidden="true" />
            <h2 className="mt-3 font-medium text-base text-foreground">
              Failed to load active client organizations
            </h2>
            <p className="mt-1 text-xs max-w-md mx-auto text-muted-foreground">
              {appError?.errorDetails ||
                "An unexpected error occurred while communicating with the server."}
            </p>
            {appError?.requestId && (
              <p className="mt-2 text-[11px] font-mono text-muted-foreground break-all">
                Request ID: {appError.requestId}
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="mt-4 bg-card text-foreground text-xs"
            >
              Try Again
            </Button>
          </div>
        ) : activeClients.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 sm:p-12 text-center shadow-card">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              <Building2 className="h-6 w-6" aria-hidden="true" />
            </div>
            {hasSearchFilter ? (
              <>
                <h2 className="mt-4 font-medium text-base text-foreground">
                  No active clients match your search
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  Try searching with a different client name, contact person, or city.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClearSearch}
                  className="mt-4 text-xs touch:min-h-[44px]"
                >
                  Clear search
                </Button>
              </>
            ) : (
              <>
                <h2 className="mt-4 font-medium text-base text-foreground">
                  No active client organizations available
                </h2>
                <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
                  Only organizations in Active status can hold assigned hardware devices.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  asChild
                  className="mt-4 text-xs touch:min-h-[44px]"
                >
                  <Link to="/s-admin/clients">Go to Client Management</Link>
                </Button>
              </>
            )}
          </div>
        ) : (
          <>
            <ActiveClientsList
              clients={activeClients}
              onManage={handleManageClient}
            />

            {pagination && pagination.total_pages > 1 && (
              <nav
                aria-label="Active clients pagination"
                className="flex items-center justify-between pt-2 text-xs text-muted-foreground tabular-nums"
              >
                <p className="font-mono">
                  {(pagination.page - 1) * pagination.page_size + 1}–
                  {Math.min(
                    pagination.page * pagination.page_size,
                    pagination.total_count
                  )}{" "}
                  of {pagination.total_count}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!pagination.has_prev}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="h-10 sm:h-8 touch:min-h-[44px] gap-1 text-xs"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                    <span>Previous</span>
                  </Button>
                  <span className="font-mono px-1">
                    {pagination.page} / {pagination.total_pages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!pagination.has_next}
                    onClick={() => setPage((p) => p + 1)}
                    className="h-10 sm:h-8 touch:min-h-[44px] gap-1 text-xs"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </nav>
            )}
          </>
        )}
      </div>
    </SectionErrorBoundary>
  );
}

